"use client"

import { useEffect, useRef } from "react"

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)"

/**
 * Scrolls `el` to the top of the viewport (honoring its scroll-margin) and
 * moves focus to its heading: the first `[data-result-heading]`, else the
 * first h1-h3, else `el`. Reduced motion jumps instead of smooth-scrolling;
 * focus still moves. For tools with their own timing rules around the handoff.
 */
export function handOffToResult(el: HTMLElement) {
  const reduce = window.matchMedia?.(REDUCED_MOTION_QUERY).matches ?? false
  el.scrollIntoView({ block: "start", behavior: reduce ? "auto" : "smooth" })

  const target =
    el.querySelector<HTMLElement>("[data-result-heading]") ??
    el.querySelector<HTMLElement>("h1, h2, h3") ??
    el
  if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1")
  target.focus({ preventScroll: true })
}

/**
 * Hands the viewport and keyboard focus to a result that appears after an
 * explicit submit, so the user lands at the top of the answer instead of at
 * the old scroll depth.
 *
 * Pass a value that changes once per computed result (the result object, or
 * an id). Each time it becomes a new non-null value, the returned ref's element
 * scrolls to the top of the viewport and its heading takes focus. A result that
 * is already present on the first render (restored from the URL) is left
 * alone, so a page load never moves the viewport.
 *
 * Give the element a `scroll-mt-*` class to clear the sticky header; see
 * handOffToResult for the focus target. Pass a boolean ("a result exists") to
 * hand off only the first time a result appears. Tools that recompute on every
 * keystroke before the user is done with the form should not use this: the
 * handoff would pull the viewport away mid-edit.
 */
export function useResultHandoff<T extends HTMLElement = HTMLDivElement>(trigger: unknown) {
  const ref = useRef<T>(null)
  const previous = useRef<unknown>(trigger)

  useEffect(() => {
    const last = previous.current
    previous.current = trigger
    if (trigger == null || trigger === false || trigger === last) return

    if (ref.current) handOffToResult(ref.current)
  }, [trigger])

  return ref
}
